'use client';

import { useCallback, useEffect, useRef } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import useVehicleFiltersStore from '@/store/useVehicleFiltersStore';
import {
  clearedSearchParams,
  filtersToSearchParams,
  hayFiltrosEnUrl,
  searchParamsToFilters,
} from '@/lib/vehicleFiltersUrl';

/**
 * Escribe la URL sin pasar por el router. `router.replace` vuelve a pedir el RSC
 * de la ruta (y /vehicles es dinámica: cada pedido cruza el middleware y su
 * consulta de tenant). Next parchea `history.replaceState` para refrescar
 * `useSearchParams`, así que un click en un checkbox no toca el servidor.
 */
const writeUrl = (url: string) => {
  if (typeof window === 'undefined') return;
  window.history.replaceState(null, '', url);
};

/**
 * Mantiene los filtros del listado y la URL en el mismo estado.
 *
 * - Al montar, lo que venga en la URL manda sobre el store (así el link que
 *   comparte el vendedor abre el listado ya filtrado en el navegador del cliente).
 * - Después, cada cambio de filtro se escribe con `replace` para no llenar el
 *   historial con un paso por cada checkbox.
 *
 * El bucle lectura↔escritura se corta con dos guardas: la lectura corre una sola
 * vez, y la escritura sólo dispara si el query string calculado difiere del real.
 *
 * `ready` es false mientras cargan los vehículos: sin ellos `maxPrice` vale 0 y el
 * rango de precio de la URL se vería como "sin filtrar", borrándolo del link.
 *
 * `categoryTab` es OPCIONAL y existe por el listado del home
 * (NewVehiclesSection), que además de los filtros del store tiene una pestaña de
 * categoría en estado local. /vehicles no la tiene y llama al hook sin ese
 * argumento, con lo que su query string sale exactamente igual que antes.
 */
export interface CategoryTabSync {
  /** Valor actual, ya traducido a lo que va en la URL. */
  value: string;
  /** Recibe lo que venía en la URL al abrir el link. */
  onChange: (value: string) => void;
}

export function useVehicleFiltersUrl(
  maxPrice: number,
  ready: boolean,
  categoryTab?: CategoryTabSync
) {
  const searchParams = useSearchParams();
  const pathname = usePathname();

  const filters = useVehicleFiltersStore((s) => s.filters);
  const priceRange = useVehicleFiltersStore((s) => s.priceRange);
  const sortOrder = useVehicleFiltersStore((s) => s.sortOrder);
  const searchQuery = useVehicleFiltersStore((s) => s.searchQuery);
  const setFilters = useVehicleFiltersStore((s) => s.setFilters);
  const setPriceRange = useVehicleFiltersStore((s) => s.setPriceRange);
  const setSortOrder = useVehicleFiltersStore((s) => s.setSortOrder);
  const setSearchQuery = useVehicleFiltersStore((s) => s.setSearchQuery);

  const hydrated = useRef(false);
  /** Mientras "Limpiar filtros" vacía la URL, nadie más puede escribirla. */
  const clearing = useRef(false);

  // Por ref: quien llama arma un objeto nuevo en cada render y meterlo en las
  // dependencias del efecto lo dispararía en bucle. Sólo el VALOR entra como
  // dependencia de la escritura, más abajo.
  const tabRef = useRef(categoryTab);
  tabRef.current = categoryTab;
  const tabValue = categoryTab?.value;

  useEffect(() => {
    if (hydrated.current) return;
    hydrated.current = true;

    const parsed = searchParamsToFilters(
      new URLSearchParams(searchParams.toString())
    );
    if (!parsed.hasAny) return;

    setFilters(parsed.filters);
    if (parsed.priceRange) setPriceRange(parsed.priceRange);
    if (parsed.sortOrder) setSortOrder(parsed.sortOrder);
    if (parsed.searchQuery !== null) setSearchQuery(parsed.searchQuery);
    // La URL manda sobre lo que hubiera en memoria: un link compartido tiene que
    // abrir la misma búsqueda en el navegador del que lo recibe.
    if (parsed.categoryTab) tabRef.current?.onChange(parsed.categoryTab);
  }, [searchParams, setFilters, setPriceRange, setSortOrder, setSearchQuery]);

/**
   * Vacía los filtros de la URL, y también la campaña (`promo`/`titulo`). No basta
   * con dejar que el efecto de escritura la recalcule: ese efecto preserva
   * `promo`, y correría con el `searchParams` viejo, volviendo a pegar la campaña
   * que acabamos de sacar.
   *
   * Sobreviven lo FUNCIONAL (`section`) y el ORDEN (`orden`). `section` no es
   * una preferencia que el usuario esté limpiando, es la sección que /embed
   * necesita para montar algo: sin esa excepción, limpiar filtros dentro de un
   * iframe lo dejaba en blanco. `orden` porque ordenar no es filtrar, y el
   * store también lo conserva al limpiar. En /vehicles `section` no existe.
   */
  const clearUrl = useCallback(() => {
    if (!searchParams.toString()) return;
    clearing.current = true;
    const quedan = clearedSearchParams(
      new URLSearchParams(searchParams.toString())
    ).toString();
    writeUrl(quedan ? `${pathname}?${quedan}` : pathname);
  }, [searchParams, pathname]);

  useEffect(() => {
    if (!hydrated.current || !ready) return;

    if (clearing.current) {
      // Antes se esperaba a ver la URL VACÍA. Ahora limpiar puede dejar algo
      // funcional (section) y el orden, así que la señal es que ya no queden
      // FILTROS. `hasAny` no sirve acá: cuenta `orden`, que se conserva.
      if (hayFiltrosEnUrl(new URLSearchParams(searchParams.toString()))) {
        return;
      }
      clearing.current = false;
    }

    const current = new URLSearchParams(searchParams.toString());
    const next = filtersToSearchParams(
      { filters, priceRange, sortOrder, searchQuery, categoryTab: tabValue },
      maxPrice,
      current
    );

    const nextStr = next.toString();
    if (nextStr === current.toString()) return;

    writeUrl(nextStr ? `${pathname}?${nextStr}` : pathname);
  }, [
    filters,
    priceRange,
    sortOrder,
    searchQuery,
    tabValue,
    maxPrice,
    ready,
    searchParams,
    pathname,
  ]);

  /**
   * Resincroniza la URL después de un `popstate`.
   *
   * El drawer del celular empuja una entrada de historial al abrir y la
   * consume con `history.back()` al cerrar (`useCerrarConAtras`). Si el
   * usuario elige un orden o un filtro y el drawer se cierra en el MISMO
   * clic, el `back()` sale antes que la escritura de arriba: la URL nueva
   * se escribe en la entrada que se está abandonando y, al llegar el
   * `popstate`, el navegador vuelve a la entrada anterior sin el parámetro.
   * En producción `useSearchParams` no vuelve a disparar el efecto de
   * escritura tras ese `popstate`, así que la URL quedaba sin `orden=` o
   * sin `marca=` aunque el listado sí estuviera ordenado o filtrado.
   *
   * Acá se mira la URL REAL (`window.location`) tras cada `popstate` y, si
   * no coincide con el store, se vuelve a escribir. El `setTimeout(0)` deja
   * que el router de Next procese primero su propio `popstate`. Sólo actúa
   * si seguimos en la misma ruta: si Atrás llevó a otra página, no es
   * asunto de este hook y no hay que ensuciar esa URL.
   */
  const estadoRef = useRef({ filters, priceRange, sortOrder, searchQuery, tabValue, maxPrice, ready });
  estadoRef.current = { filters, priceRange, sortOrder, searchQuery, tabValue, maxPrice, ready };

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const resincronizar = () => {
      setTimeout(() => {
        const e = estadoRef.current;
        if (!hydrated.current || !e.ready || clearing.current) return;
        if (window.location.pathname !== pathname) return;
        const current = new URLSearchParams(window.location.search);
        const next = filtersToSearchParams(
          {
            filters: e.filters,
            priceRange: e.priceRange,
            sortOrder: e.sortOrder,
            searchQuery: e.searchQuery,
            categoryTab: e.tabValue,
          },
          e.maxPrice,
          current
        );
        const nextStr = next.toString();
        if (nextStr === current.toString()) return;
        writeUrl(nextStr ? `${pathname}?${nextStr}` : pathname);
      }, 0);
    };
    window.addEventListener('popstate', resincronizar);
    return () => window.removeEventListener('popstate', resincronizar);
  }, [pathname]);

  return { clearUrl };
}
